import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase, hasSupabaseCredentials } from '../lib/supabaseClient';

export interface Profile {
  id: string;
  full_name: string;
  role: string;
  experience_level: string;
  weekly_goal_minutes: number;
  current_streak: number;
  onboarding_completed: boolean;
  plan: 'free' | 'starter' | 'pro' | 'team';
  current_team_id: string | null;
  plan_renews_at: string | null;
  public_handle: string | null;
  public_profile_enabled: boolean;
  public_bio: string;
  workspace_id: string | null;
  preferred_tool: 'chatgpt' | 'claude' | 'gemini';
  xp: number;
  level: number;
  longest_streak: number;
  streak_freezes: number;
  last_active_at: string | null;
  preferred_difficulty: 'easy' | 'medium' | 'hard' | 'mixed';
  is_admin: boolean;
  account_type: 'adult' | 'kid';
  active_kid_profile_id: string | null;
  daily_xp_goal: number;
  reading_level: 'standard' | 'simple';
}

interface AuthContextType {
  user: (User & { isSubscribed?: boolean }) | null;
  profile: Profile | null;
  loading: boolean;
  remainingTrialPrompts: number;
  decrementTrialPrompts: () => void;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  profile: null,
  loading: true,
  remainingTrialPrompts: 5,
  decrementTrialPrompts: () => {},
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  refreshProfile: async () => {},
  updateProfile: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<(User & { isSubscribed?: boolean }) | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [remainingTrialPrompts, setRemainingTrialPrompts] = useState(5);

  const decrementTrialPrompts = () => setRemainingTrialPrompts((p) => Math.max(0, p - 1));

  const loadProfile = useCallback(async (userId: string) => {
    if (!hasSupabaseCredentials) return;
    try {
      const { data } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle();
      if (data) {
        setProfile(data as Profile);
        return;
      }
      const { data: created, error: upsertError } = await supabase
        .from('profiles')
        .upsert({ id: userId, full_name: '' }, { onConflict: 'id' })
        .select()
        .maybeSingle();
      if (upsertError) {
        console.error('Profile upsert error:', upsertError);
        return;
      }
      if (created) setProfile(created as Profile);
    } catch (e) {
      console.error('Profile load error:', e);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (user) await loadProfile(user.id);
  }, [user, loadProfile]);

  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', user.id)
      .select()
      .maybeSingle();
    if (error) {
      toast.error('Could not save profile');
      return;
    }
    if (data) setProfile(data as Profile);
  };

  useEffect(() => {
    if (!hasSupabaseCredentials) {
      setLoading(false);
      return;
    }

    let mounted = true;
    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      if (session?.user) {
        setUser(session.user);
        await loadProfile(session.user.id);
      }
      setLoading(false);
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      (async () => {
        if (session?.user) {
          setUser(session.user);
          await loadProfile(session.user.id);
        } else {
          setUser(null);
          setProfile(null);
        }
      })();
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signUp = async (email: string, password: string, fullName: string) => {
    if (!hasSupabaseCredentials) {
      toast.error('Authentication not configured');
      return;
    }
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName } },
    });
    if (error) {
      toast.error(error.message);
      throw error;
    }
    toast.success('Account created');
  };

  const signIn = async (email: string, password: string) => {
    if (!hasSupabaseCredentials) {
      toast.error('Authentication not configured');
      return;
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      toast.error(error.message);
      throw error;
    }
    toast.success('Welcome back');
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    toast.success('Signed out');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        remainingTrialPrompts,
        decrementTrialPrompts,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
